import { nestApiClient } from './api-client';

import {
  LeaderboardRankResponse,
  LeaderboardRankResponseObject,
  UserRankResponse,
  EventType,
  EventTypeEnum,
  IndividualEventResponse,
  GlobalEventResponse,
  UserSoloEventResponse,
  LeaderboardRewardClaimStatusResponse,
  UserRbeLeaderboardResponse,
  ScheduledEventResponse,
} from '@wwe-portal/entities/rbe.type';
import axios from 'axios';

export const getGlobalEvents = () => {
  return nestApiClient.get<GlobalEventResponse>('/rbe/v2?hasLeaderboard=false');
};

export const getScheduledGlobalEvents = () => {
  return nestApiClient.get<ScheduledEventResponse>('/v1/events/scheduled');
};

export const getSoloEvents = () => {
  return nestApiClient.get<IndividualEventResponse>(
    '/rbe/v2/loggedOut/individual'
  );
};

export const getUserSoloEvents = () => {
  return nestApiClient.get<UserSoloEventResponse>('/rbe/v2/individual');
};

export const getAllRewardsByStatus = (status: string) => {
  return nestApiClient.get<LeaderboardRewardClaimStatusResponse>(
    `/v1/events/all/claim-status/${status}`
  );
};

export const getRbeLeaderboardByEventId = (
  eventId: string | number,
  params?: { isActive: boolean }
) => {
  return nestApiClient.get<UserRbeLeaderboardResponse>(
    `/rbe-leaderboards/${eventId}`,
    { params }
  );
};

export const updateClaimRewardStatuses = (eventId: string) =>
  nestApiClient.put(`/v1/events/${eventId}/claim-status/all`, {
    newStatus: 'SHOWED',
  });

export const claimRbeMilestoneReward = (payload: {
  eventId: number;
  milestoneId: string;
}) => {
  return nestApiClient.post(`/rbe/v2/claim`, payload);
};

export const claimAllMilestones = (eventId: number) => {
  return nestApiClient.post(`/v1/events/${eventId}/milestones/all/claim`);
};

export const updateLeaderboardPublicity = ({
  isAnonymous,
  leaderboardId,
}: {
  isAnonymous: boolean;
  leaderboardId: string;
}) => {
  return nestApiClient.put(`/rbe-leaderboards/${leaderboardId}/users`, {
    isAnonymous,
  });
};

export const getTopSoloRbeLeaderboard = async (portalEventId: string) => {
  const response = await nestApiClient.get<LeaderboardRankResponse>(
    `/rbe-leaderboards/${portalEventId}/solo/rank`
  );
  const data = response.data.data;
  if (typeof data === 'string') {
    return JSON.parse(data) as Partial<LeaderboardRankResponseObject>;
  }

  return data;
};

export const getTopGlobalRbeLeaderboard = async (portalEventId: string) => {
  const response = await nestApiClient.get<LeaderboardRankResponse>(
    `/rbe-leaderboards/${portalEventId}/global/rank`
  );
  const data = response.data.data;
  if (typeof data === 'string') {
    return JSON.parse(data) as Partial<LeaderboardRankResponseObject>;
  }

  return data;
};

export const getUserRankInSoloLeaderboard = (portalEventId: string) => {
  return nestApiClient.get<UserRankResponse>(
    `/rbe-leaderboards/${portalEventId}/solo/my-rank`
  );
};

export const getUserRankInGlobalLeaderboard = (portalEventId: string) => {
  return nestApiClient.get<UserRankResponse>(
    `/rbe-leaderboards/${portalEventId}/global/my-rank`
  );
};

const tryGetTopRbeLeaderboard = async ({
  portalEventId,
  typeId,
}: {
  portalEventId: string;
  typeId: EventType;
}) => {
  try {
    if (typeId === EventTypeEnum.SOLO) {
      return await getTopSoloRbeLeaderboard(portalEventId);
    }
    if (typeId === EventTypeEnum.GLOBAL) {
      return await getTopGlobalRbeLeaderboard(portalEventId);
    }
  } catch (e) {
    if (axios.isAxiosError(e)) {
      if (e.response?.status === 404) return undefined;
    }
    throw e;
  }
};

const tryGetUserRank = async (
  { portalEventId, typeId }: { portalEventId: string; typeId: EventType },
  isAuthenticated: boolean
) => {
  try {
    const defaultData = {
      data: {
        currentPlayer: undefined,
        abovePlayer: undefined,
      },
    };
    if (!isAuthenticated) return defaultData;

    if (typeId === EventTypeEnum.SOLO) {
      const response = await getUserRankInSoloLeaderboard(portalEventId);
      return response.data;
    }

    if (typeId === EventTypeEnum.GLOBAL) {
      const response = await getUserRankInGlobalLeaderboard(portalEventId);
      return response.data;
    }
  } catch (e) {
    if (axios.isAxiosError(e) && e.response?.status === 404) {
      return undefined;
    }
    throw e;
  }
};

export const getLeaderboard = async ({
  portalEventId,
  typeId,
  isAuthenticated,
}: {
  portalEventId: string;
  isAuthenticated: boolean;
  typeId: EventType;
}) => {
  const promises: [
    Promise<Partial<LeaderboardRankResponseObject | undefined>>,
    Promise<UserRankResponse | undefined>,
  ] = [
    tryGetTopRbeLeaderboard({ portalEventId, typeId }),
    tryGetUserRank({ portalEventId, typeId }, isAuthenticated),
  ];

  const [leaderboardData, userRankData] = await Promise.all(promises);
  return {
    ...leaderboardData,
    abovePlayer: userRankData?.data?.abovePlayer,
    currentPlayer: userRankData?.data?.currentPlayer,
  };
};
